import { NextPageContext, NextComponentType } from 'next';
import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject,
} from '@apollo/client';
import initApollo from '@utils/apollo/initApollo';
import { getDataFromTree } from '@apollo/client/react/ssr';
import { NextRouter } from 'next/router';
import logger from '@utils/logger';
import {
  AppContextType,
  AppInitialProps,
  AppPropsType,
} from 'next/dist/shared/lib/utils';
import { Channel } from '@monofront/business/constants';
import { getChannelFromLocale } from '@monofront/business/utils';
import { NextIntlClientProvider } from 'next-intl';
import {
  COOKIE_AUTHENTICATED,
  COOKIE_PERSISTED_CUSTOMER,
} from '@constants/cookies';

interface ApolloAppContextType extends AppContextType<NextRouter> {
  ctx: NextPageContext & {
    apolloClient?: ApolloClient<NormalizedCacheObject>;
  };
}

interface IApolloInitialProps {
  apolloState: NormalizedCacheObject;
  channel: Channel;
  cookieString: string;
}

type ApolloAppType = NextComponentType<
  AppContextType,
  AppInitialProps & IApolloInitialProps,
  AppPropsType &
    IApolloInitialProps & {
      apolloClient: ApolloClient<NormalizedCacheObject>;
    }
>;

/**
 * Cookies that are read while rendering (and therefore needed by the
 * server-side `CookiesProvider`). On the client, `universal-cookie` reads
 * `document.cookie` directly, so nothing else has to be forwarded.
 */
const SSR_COOKIES = [COOKIE_AUTHENTICATED, COOKIE_PERSISTED_CUSTOMER];

/**
 * Keeps only the allowlisted cookies from the request header. The result is
 * serialized into `__NEXT_DATA__`, so every other cookie — httpOnly or
 * third-party — must never reach it.
 * @param cookieHeader the raw `Cookie` request header
 * @returns a cookie string containing the allowlisted cookies only
 */
const pickSSRCookies = (cookieHeader = ''): string =>
  cookieHeader
    .split(';')
    .filter((pair) => SSR_COOKIES.includes(pair.split('=')[0].trim()))
    .join(';');

const withApollo = (App: ApolloAppType) => {
  const WithApollo = (props: AppPropsType & IApolloInitialProps) => {
    const apolloClient = initApollo(props.channel, props.apolloState);

    return <App apolloClient={apolloClient} {...props} />;
  };

  WithApollo.displayName = 'withApollo(App)';

  WithApollo.getInitialProps = async (
    appContext: ApolloAppContextType,
  ): Promise<AppInitialProps & IApolloInitialProps> => {
    const { AppTree, ctx, Component, router } = appContext;

    const channel = getChannelFromLocale(ctx.locale);

    let appProps: AppInitialProps = { pageProps: {} };

    if (App.getInitialProps) {
      appProps = await App.getInitialProps(appContext);
    }

    if (ctx.res?.writableEnded) {
      return { pageProps: {}, apolloState: {}, channel, cookieString: '' };
    }

    const apollo = initApollo(channel);
    const isRunningOnServer = typeof window === 'undefined';

    if (isRunningOnServer && Component.getInitialProps) {
      try {
        await getDataFromTree(
          <ApolloProvider client={apollo}>
            <NextIntlClientProvider
              locale={ctx.locale}
              messages={appProps.pageProps.messages}
            >
              <AppTree
                {...appProps}
                apolloClient={apollo}
                Component={Component}
                router={router}
              />
            </NextIntlClientProvider>
          </ApolloProvider>,
        );
      } catch (e: any) {
        logger.error('Error while running getDatafromTree', e);
      }
    }

    const apolloState = apollo.cache.extract();

    return {
      ...appProps,
      apolloState,
      channel,
      cookieString: pickSSRCookies(ctx.req?.headers?.cookie),
    };
  };

  return WithApollo;
};

export default withApollo;
